import React from 'react';
import { usePallet } from '../contexts/PalletContext';
import { TextField, Autocomplete, createFilterOptions } from '@mui/material';
import useStyles from './layout/Styles';

type Option = { value: any; label?: string; name?: string };

export default function SelectTextFieldA(props: {
    onChange?: (e: { target: { value: any; name?: string; id?: string } }) => void,
    value: any,
    options: Option[],
    label?: string,
    id: string,
    name?: string,
    placeholder?: string,
    focused?: boolean,
    disabled?: boolean,
    defaultValue?: any,
    altura?: string
}) {
    const classes = useStyles();
    const { pallet } = usePallet();

    const options = React.useMemo(() => props.options ?? [], [props.options]);

    const selectedOption = React.useMemo(() => {
        const v = props.value ?? props.defaultValue;
        return options.find((o) => o?.value === v) ?? null;
    }, [options, props.value, props.defaultValue]);

    const [inputValue, setInputValue] = React.useState('');

    React.useEffect(() => {
        if (selectedOption) {
            const lbl = selectedOption.label ?? selectedOption.name ?? String(selectedOption.value ?? '');
            setInputValue(lbl);
        } else {
            setInputValue('');
        }
    }, [selectedOption]);

    const filter = createFilterOptions<Option>({
        stringify: (opt) => `${opt.label ?? ''} ${opt.name ?? ''} ${opt.value ?? ''}`,
        ignoreAccents: true,
        trim: true,
    });

    const handleChange = (_: any, newOption: Option | null) => {
        props.onChange?.({
            target: { value: newOption ? newOption.value : '', name: props.name, id: props.id },
        });
    };

    const handleInputChange = (_: any, newInput: string, reason: string) => {
        if (reason === 'input') setInputValue(newInput);
    };

    const stopKeyPropagation = (e: React.KeyboardEvent) => {
        e.stopPropagation();
    };

    return (
        <Autocomplete
            id={props.id}
            options={options}
            value={selectedOption}
            inputValue={inputValue}
            onChange={handleChange}
            onInputChange={handleInputChange}
            disablePortal
            disableCloseOnSelect={false}
            openOnFocus={false}
            clearOnBlur={false}
            selectOnFocus
            handleHomeEndKeys
            disabled={!!props.disabled}
            isOptionEqualToValue={(opt, val) => opt?.value === val?.value}
            getOptionLabel={(opt: Option) =>
                opt?.label ?? opt?.name ?? (opt?.value != null ? String(opt.value) : '')
            }
            filterOptions={filter}
            renderOption={(optionProps, option) => (
                <li
                    {...optionProps}
                    key={`${option?.value ?? optionProps.id}`}
                    style={{
                        fontFamily: pallet.general.fontFamily,
                        maxWidth: '600px',
                        whiteSpace: 'pre-line',
                        borderBottom: '1px solid #D3D3D3',
                        margin: '5px',
                        wordWrap: 'break-word',
                    }}
                    title={`${option?.label ?? ''} ${option?.name ?? ''}`}
                    onKeyDown={stopKeyPropagation}
                >
                    {option?.label} {option?.name}
                </li>
            )}
            renderInput={(params) => (
                <TextField
                    {...params}
                    label={props.label}
                    placeholder={props.placeholder ?? 'Digite para buscar...'}
                    variant="outlined"
                    focused={!!props.focused}
                    onKeyDown={stopKeyPropagation}
                    InputProps={{
                        ...params.InputProps,
                        classes: { notchedOutline: classes.notchedOutline },
                        style: {
                            fontFamily: pallet.general.fontFamily,
                            fontSize: pallet.general.size14,
                            fontWeight: pallet.general.weight_100,
                            height: props.altura,
                            wordWrap: 'break-word',
                        },
                    }}
                    InputLabelProps={{ style: { color: 'grey' } }}
                    fullWidth
                />
            )}
            sx={{
                width: '100%',
                '& .MuiOutlinedInput-root': { height: props.altura },
            }}
        />
    );
}